<template>
  <v-card class="rounded-card card-shadow mt-3">
    <v-card-text class="text--primary">
      <v-row dense>
        <v-col cols="12" sm="8">
          <px-autocomplete-produto dense
                                   show-details
                                   menu-props="auto"
                                   v-model="produto.produto"
                                   class="required rounded-tag-small"
                                   :only-cotavel="false"
                                   base-path="/produtos/page"
                                   :error="$v.produto.produto.$error"
                                   @blur="$v.produto.produto.$touch()"
                                   :truncate-value="60"
                                   required/>
        </v-col>
        <v-col cols="12" sm="4">
          <px-autocomplete-unidade-medida dense
                                          required
                                          class="required rounded-tag-small"
                                          v-model="produto.unidadeMedida"
                                          :onlyOpen="false"
                                          :error="$v.produto.unidadeMedida.$error"
                                          @blur="$v.produto.unidadeMedida.$touch()"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="3">
          <px-input-decimal-number
            dense
            required
            label="Quantidade"
            class="required rounded-tag-small"
            :precision="precision"
            :error="$v.produto.quantidade.$error"
            @blur="$v.produto.quantidade.$touch()"
            v-model="produto.quantidade"/>
        </v-col>
        <v-col cols="3">
          <px-input-currency
            dense
            label="Preço unitário"
            class="required rounded-tag-small"
            ref="precoUnitario"
            v-model="produto.precoUnitario"
            :options="configuracaoMoeda"
            :precision="precision"
            :error="$v.produto.precoUnitario.$error"
            @blur="$v.produto.precoUnitario.$touch()"/>
        </v-col>
        <v-col cols="3">
          <px-input-percentual
            dense
            label="ICMS %"
            class="required rounded-tag-small"
            ref="icms"
            :error="$v.produto.impostos.icms.$error"
            @blur="$v.produto.impostos.icms.$touch()"
            v-model="produto.impostos.icms"/>
        </v-col>
        <v-col cols="3">
          <px-input-percentual
            dense
            label="IPI %"
            class="required rounded-tag-small"
            ref="ipi"
            :error="$v.produto.impostos.ipi.$error"
            @blur="$v.produto.impostos.ipi.$touch()"
            v-model="produto.impostos.ipi"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="10">
          <v-textarea
            v-if="isEditing && isFromCotacao"
            dense
            hide-details
            outlined
            auto-grow
            v-model="produto.especificacaoProdutoOrigem"
            label="Especificação"
            rows="1"
            class="rounded-tag-small"
          />
          <v-textarea
            v-else
            dense
            hide-details
            outlined
            auto-grow
            v-model="produto.especificacaoProduto"
            label="Especificação"
            rows="1"
            class="rounded-tag-small"
          />
        </v-col>
        <v-col cols="12" sm="2">
          <px-cadastro-produto-observacoes
            @file-loading="onFileLoad"
            :produto="produto"
            :small="false"/>
          <px-cadastro-produto-campos-adicionais
            show-cadastros-adicionais
            show-nota-entrada
            dense
            ref="camposAdicionais"
            :campos-obrigatorios="camposObrigatorios"
            :produto="produto"
            :small="false"
            :cadastros-adicionais="produto.cadastrosAdicionais"/>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click="onRemove">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Remover item</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import validationMixin from '@/mixins/validation-mixin';
import { OrigemCompra } from 'px-business-components';
import {
  computeFrom,
  initialStateCamposErp,
  validateCamposErp,
} from '@/modules/carrinho/finalizacao/item-campos-erp/validate-campos-erp';
import { getPrecision } from '@/utils/produto-utils';
import validationFileMixin from '@/mixins/validation-file-mixin';
import {
  initialStateImpostos,
  mapValoresTabelaPreco,
} from '@/modules/compra/cadastro/compra-utils';
import { formatToCode } from '@/utils/moment-date-utils';
import moment from 'moment';
import TabelaPrecoService from '@/modules/tabela-preco/tabela-preco-service';
import moedaMixin from '@/mixins/moeda-mixin';

export default {
  mixins: [validationMixin, validationFileMixin, moedaMixin],
  props: {
    compra: {
      type: Object,
      required: true,
    },
    produto: {
      type: Object,
      required: true,
    },
    erpCamposIntegracao: String,
    camposObrigatorios: Object,
    isEditing: Boolean,
    isAVista: Boolean,
  },
  computed: {
    moeda() {
      return this.compra.moeda;
    },
    precision() {
      return getPrecision(this.produto.produto);
    },
    isFromCotacao() {
      return this.compra.origemCompra === OrigemCompra.keys.COTACAO.key;
    },
  },
  methods: {
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposErp(this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais);
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      return isValid;
    },
    onRemove() {
      this.$emit('remove');
    },
    validate() {
      const isFormInvalid = this.validateForm();
      const isCadastroProdutoCamposAdicionaisInvalid = this.$refs.camposAdicionais.validate();
      const isInvalid = (isFormInvalid || this.validateFile()
        || !this.validateCadastrosAdicionais()) || isCadastroProdutoCamposAdicionaisInvalid;
      return !isInvalid;
    },
    previous() {
      this.$emit('previous');
    },
    inicializarImpostos() {
      if (!this.produto.impostos) {
        this.produto.impostos = initialStateImpostos();
      }
      this.setarIcmsProduto();
      this.setarIpiProduto();
    },
    setarIcmsProduto() {
      if (this.produto.impostos.icms > 0) {
        return;
      }
      this.produto.impostos.icms = this.produto.produto.icms ?? 0;
    },
    setarIpiProduto() {
      if (this.produto.impostos.ipi > 0) {
        return;
      }
      this.produto.impostos.ipi = this.produto.produto.ipi ?? 0;
    },
    inicializaProduto() {
      this.produto.tipoNotaEntrada = this.compra.tipoNotaEntrada;
      this.produto.centroCusto = this.produto.centroCusto
        ? this.produto.centroCusto : this.compra.centroCusto;
      this.produto.projeto = this.produto.projeto
        ? this.produto.projeto : this.compra.projeto;
    },
    inicializarUnidadeMedida() {
      if (this.isEditing && this.produto.unidadeMedida.id) {
        return;
      }
      this.produto.unidadeMedida = this.produto.produto
        ? { ...this.produto.produto.unidadeMedida } : null;
    },
    inicializarCadastrosAdicionais() {
      if (this.produto.produto.cadastrosAdicionais
        && Array.isArray(this.produto.produto.cadastrosAdicionais)) {
        this.produto.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
          this.produto.produto.cadastrosAdicionais);
      }
    },
    inicializarAoSelecionarProduto() {
      if (!this.produto.produto) {
        return;
      }
      this.produto.especificacaoProduto = this.produto.produto.especificacao
        ?? this.produto.especificacaoProduto;
      this.produto.arquivos = this.produto.produto.arquivos ?? [];
      this.produto.observacaoExterna = this.produto.observacaoExterna
        ?? this.produto.produto.observacaoExterna;
      this.produto.observacaoInterna = this.produto.observacaoInterna
        ?? this.produto.produto.observacaoInterna;

      this.inicializarUnidadeMedida();
      this.inicializarImpostos();
      this.inicializarCadastrosAdicionais();
      this.getTabelaPrecoItem();
    },
    getTabelaPrecoItem() {
      const dataAtual = formatToCode(moment());
      const query = `fornecedor.id = ${this.compra.fornecedor.id} and ativo = true and produto.id = ${this.produto.produto.id} and dataVigenciaInicial <= ${dataAtual} and dataVigenciaFinal >= ${dataAtual}`;
      TabelaPrecoService.getItensTabelaPreco({
        query,
      })
        .then(({ data }) => {
          if (!data || !data.length) {
            return;
          }
          const produtoFromTabela = mapValoresTabelaPreco(this.produto, data[0], this.isAVista);
          this.produto.$$itemTabelaPreco = produtoFromTabela.$$itemTabelaPreco;
          this.produto.precoUnitario = produtoFromTabela.precoUnitario;
          this.produto.impostos = produtoFromTabela.impostos;
        });
    },
  },
  watch: {
    'produto.produto': function (newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (!newValue) {
        return;
      }
      // reseta o item da tabela se trocar o produto
      this.produto.$$itemTabelaPreco = null;
      this.inicializarAoSelecionarProduto(newValue, oldValue);
    },
    'compra.tipoNotaEntrada': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      const oldTipoNotaEntradaId = oldValue ? oldValue.id : null;
      if (!this.produto.tipoNotaEntrada
        || this.produto.tipoNotaEntrada.id === oldTipoNotaEntradaId) {
        this.produto.tipoNotaEntrada = newValue;
      }
    },
    'compra.centroCusto': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      const oldCentroCustoId = oldValue ? oldValue.id : null;
      if (!this.produto.centroCusto || this.produto.centroCusto.id === oldCentroCustoId) {
        this.produto.centroCusto = newValue;
      }
    },
    'compra.projeto': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      const oldProjetoId = oldValue ? oldValue.id : null;
      if (!this.produto.projeto || this.produto.projeto.id === oldProjetoId) {
        this.produto.projeto = newValue;
      }
    },
  },
  created() {
    if (this.produto.cadastrosAdicionais && Array.isArray(this.produto.cadastrosAdicionais)) {
      this.produto.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais);
      return;
    }
    this.produto.cadastrosAdicionais = initialStateCamposErp(this.erpCamposIntegracao);
  },
  mounted() {
    this.inicializaProduto();
  },
  validations: {
    produto: {
      produto: {
        required,
      },
      quantidade: {
        required,
      },
      unidadeMedida: {
        required,
      },
      precoUnitario: {
        required,
        minValue(value) {
          return value > 0;
        },
      },
      impostos: {
        icms: {
          required,
        },
        ipi: {
          required,
        },
      },
    },
  },
};
</script>
